import { define, inject, init, singleton } from '@injex/core';
import { computed, makeObservable, observable } from 'mobx';
import { Injex } from '@injex/webpack';
import IReportingResultsRow from '../common/interfaces/IReportingResultsRow';
import { HistoryReportsManager } from '../managers/historyReportsManager.mdl';
import { RegularReportsManager } from '../managers/regularReportsManager.mdl';
import MemoryClipboard from '../../../common/utils/MemoryClipboard';
import {ColumnsWidthApi} from '../../../common/api/ColumnsWidthApi.mdl';
import { ColumnsManager, guid } from '@vidazoo/ui-kit';
import ReportingBuildManager from '../managers/reportingBuildManager.mdl';
import IReportingResults from '../common/interfaces/IReportingResults';
import { IReportingPreparedField, IReportingPreparedResultsRow } from '../common/interfaces/IReportingPreparedResults';
import { AlertAccent } from '../../../common/alerts/common/enums';
import { AlertsManager } from '../../../common/alerts/managers/alertsManager.mdl';
import UrlsService from '../../../common/router/services/urlsService.mdl';
import { RouterService } from '../../../common/router/services/routerService.mdl';
import { GenerateReportUrlParamsManager } from '../managers/generateReportUrlParamsManager.mdl';
import { getFieldValue, getGroupValue } from '../common/utils/customGetReportingSortValue';
import { ReportHistoryStatus } from '../common/enums/ReportHistoryStatus';
import { HistoryReportGetUrlAction } from '../common/enums/HistoryReportGetUrlAction';
import { HistoryReportsColumnsManager } from '../managers/historyReportsColumnsManager.mdl';
import ReportingMetaDataManager from '../../../common/managers/ReportingMetaDataManager.mdl';
import { defaultFieldFormat } from '../common/constants/constants';
import { ReportingFieldType } from '../common/enums/ReportingFieldType';
import ReportingParamsManager from '../managers/reportingParamsManager.mdl';
import { ReportVerticalType } from '../../../common/enums/ReportVerticalType';
import ExportToCsvManager from '../../../common/managers/ExportToCsvManager.mdl';
import LocalStorageManager from '../../../common/managers/LocalStorageManager.mdl';

import IReportingEntry from '../common/interfaces/IReportingEntry';
import {compileDynamicFormula} from '../common/utils/dynamicFormula';

@define()
@singleton()
export default class ReportingModel extends ColumnsManager<IReportingPreparedResultsRow> {
  @inject() private $injex: Injex;
  @inject() private historyReportsManager: HistoryReportsManager;
  @inject() private regularReportsManager: RegularReportsManager;
  @inject() private reportingParamsManager: ReportingParamsManager;
  @inject() private reportingBuildManager: ReportingBuildManager;
  @inject() public alertsManager: AlertsManager;
  @inject() private routerService: RouterService;
  @inject() private generateReportUrlParamsManager: GenerateReportUrlParamsManager;
  @inject() private historyReportsColumnsManager: HistoryReportsColumnsManager;
  @inject() private reportingMetaDataManager: ReportingMetaDataManager;
  @inject() private exportToCsvManager: ExportToCsvManager;
  @inject() private columnsWidthApi: ColumnsWidthApi;
  @inject() public localStorageManager: LocalStorageManager;

  private savedColumnWidths: Record<string, number> = {};
  private columnWidthsLoaded = false;

  @observable public isGenerateReportDisabled: boolean;
  @observable public useHistoryReports: boolean;
  @observable public isLoading: boolean;

  @observable public dynamicFormulaDraft: {key?: string; name: string; expression: string} = null;

  public editDynamicFormula = (field: IReportingEntry) => {
    this.dynamicFormulaDraft = field.type === 'formula'
      ? {key: field.value, name: field.label, expression: field.formulaExpression || ''}
      : {name: `${field.label} Formula`, expression: `(${field.label} / Metric) * 100`};
  };

  public closeDynamicFormula = () => { this.dynamicFormulaDraft = null; };

  @computed public get formulaMetrics() {
    const metrics = new Map<string, {value: string; label: string}>();
    this.results.result[0]?.fields.filter(field => field.type !== 'formula').forEach(field =>
      metrics.set(field.key, {value: field.key, label: field.name || field.key}));
    this.reportingParamsManager.params.fields.filter(field => field.type !== 'formula').forEach(field =>
      metrics.set(field.value, {value: field.value, label: field.label}));
    return Array.from(metrics.values());
  }

  public saveDynamicFormula = (name: string, expression: string) => {
    name = name.trim();
    const draft = this.dynamicFormulaDraft;
    if (!draft) return;
    if (!name) throw new Error('Enter a name for the formula.');
    if (this.reportingParamsManager.params.fields.some(field => field.value !== draft.key
        && field.label.toLowerCase() === name.toLowerCase())) throw new Error('A metric with this name already exists.');
    compileDynamicFormula(expression, this.formulaMetrics);
    const key = draft.key || `formula_${guid()}`;
    const entry: IReportingEntry = {_id: key, value: key, label: name, type: 'formula', formulaExpression: expression};
    const fields = this.reportingParamsManager.params.fields;
    this.reportingParamsManager.setFields(draft.key
      ? fields.map(field => field.value === key ? entry : field) : [...fields, entry]);
    this.localStorageManager.recentDynamicFormulas.record(name, expression);
    this.handleSetColumns();
    this.closeDynamicFormula();
  };

  @computed private get dynamicFormulas() {
    return this.reportingParamsManager.params.fields.filter(field => field.type === 'formula').map(field => {
      let evaluate: (values: Record<string, number>) => number;
      try { evaluate = compileDynamicFormula(field.formulaExpression || '', this.formulaMetrics); }
      catch { evaluate = () => 0; }
      return {field, evaluate};
    });
  }

  @observable public executionTime: number;
  @observable public startTime: number;

  @observable public reportIsEmpty: boolean;
  @observable public results: IReportingResults;
  /** Row that every other row is compared to; cleared whenever the results change. */
  @observable public baselineRowId: string | null = null;

  public setBaselineRow = (rowId: string) => {
    this.baselineRowId = rowId;
  };

  public clearBaselineRow = () => {
    this.baselineRowId = null;
  };

  @computed
  public get baselineRow(): IReportingPreparedResultsRow | null {
    if (this.baselineRowId === null) return null;
    return this.data.find((row) => row.id === this.baselineRowId) ?? null;
  }

  /** Baseline row fields by key (includes dynamic formulas). */
  @computed
  public get baselineFields(): Map<string, IReportingPreparedField> | null {
    const row = this.baselineRow;
    return row ? new Map(row.fields.map((field) => [field.key, field])) : null;
  }

  constructor() {
    super({
      persistKey: 'reporting_list_columns',
    });

    this.columns = {};
    this.tableColumns = [];
    this.isLoading = false;
    this.useHistoryReports = false;
    this.results = this.initialResults;
    makeObservable(this);
  }

  @init()
  public init() {
    this.connectHooks();
  }

  public async getReport() {
    try {
      this.startTime = Date.now();

      // Table searches and filters are kept per column; one left on a removed
      // dimension would hide every row of the new report.
      this.resetSearch();
      this.resetFilter();
      this.isLoading = true;
      await this.loadSavedColumnWidths();
      this.handleSetColumns();

      this.setGenerateReportStatus();
      this.reportingBuildManager.setRequestedParams(this.reportingParamsManager.params);

      if (this.useHistoryReports && this.historyReportsManager.allowedHistoryReports) {
        await this.historyReportsManager.get();
      } else {
        await this.regularReportsManager.get();
      }
    } catch (e) {
      this.onReportError(e);
    }
  }

  public onReportReady = async (data: IReportingResultsRow[]) => {
    this.clearBaselineRow();
    if (!data || data.length === 0) {
      this.alertsManager.setBasicAlert(
        { content: `We couldn\'t find any results` },
        AlertAccent.Warning
      );
      this.isLoading = false;
      this.reportIsEmpty = true;
      return;
    }
    this.handleSetColumns();
    this.reportIsEmpty = false;
    this.results = await this.reportingBuildManager.buildResults(data);
    this.isLoading = false;
    this.executionTime = Date.now() - this.startTime;
  };

  private async loadSavedColumnWidths() {
    if (this.columnWidthsLoaded) return;
    this.columnWidthsLoaded = true;
    try {
      const response = await this.columnsWidthApi.getColumnsWidth('reporting');
      if (response?.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        this.savedColumnWidths = response.data;
      }
    } catch (error) {
      // Optional preferences must not prevent a report from loading.
      this.$injex.logger.warn('Could not load saved reporting column widths');
    }
  }

  public handleSetColumns = () => {
    let columns = [];
    const groupColumn = (title: string, key: string) => ({
      title,
      key,
      defaultWidth: 200,
      getValue: getGroupValue,
      options: {
        order: false,
        search: { searchKey: key },
        freeze: true,
        sort: { sortKey: key, sortOnHeaderClick: true },
      },
    });
    this.reportingParamsManager.params.groups.forEach((group) => columns.push(groupColumn(group.label, group.value)));
    this.reportingParamsManager.params.subGroups.forEach((subGroup) => {
      subGroup.entities.forEach((entity) => {
        if (entity === 'targeting') {
          subGroup.targeting.types.forEach((targetingType) => {
            subGroup.targeting.operators.forEach((targetingOperator) => {
              const entityTargeting = `targeting:${targetingType}:${targetingOperator}`;
              columns.push(groupColumn(entityTargeting, entityTargeting));
            });
          });
        } else if (entity === 'optionsWidget' && subGroup.freeField) {
          const key = `optionsWidget:${subGroup.freeField}`;
          columns.push(groupColumn(key, key));
        } else {
          columns.push(groupColumn(entity, entity));
        }
      });
    });

    // Results keep showing the dimensions they were generated with until the next report,
    // so a dimension removed meanwhile still needs a column (sorting/searching its header reads it).
    if (this.results?.result?.length) {
      const keys = new Set(columns.map((column) => column.key));
      (this.reportingBuildManager.requestedGroups || []).forEach((group) => {
        if (!keys.has(group.value)) {
          keys.add(group.value);
          columns.push(groupColumn(group.label, group.value));
        }
      });
    }

    this.reportingParamsManager.params.fields.forEach((field) =>
      columns.push({
        title: field.label,
        key: field.value,
        defaultWidth: 200,
        getValue: getFieldValue,
        options: {
          order: false,
          search: { searchKey: field.value },
          freeze: true,
          sort: { sortKey: field.value, sortOnHeaderClick: true },
        },
      })
    );
    this.resetOrder();
    this.setColumns(observable.array(columns));
    // The legacy API keys widths by display label; keep subsequent local resizing.
    columns.forEach(column => {
      const savedWidth = this.savedColumnWidths[column.title];
      if ((!this.columns[column.key] || this.getWidth(column.key) === 200)
          && typeof savedWidth === 'number' && Number.isFinite(savedWidth) && savedWidth >= 50) {
        this.setWidth(column.key, savedWidth);
      }
    });
  };

  public setColumnPosition(colKey: string, nextIndex: number) {
    super.setColumnPosition(colKey, nextIndex);
    const groups = [];
    const fields = [];
    this.columnsOrder.forEach((key) => {
      const group =
        this.reportingMetaDataManager.metaDataByVertical[ReportVerticalType.PLATFORM]?.groups.get(
          key
        );
      if (group) {
        groups.push(group);
      }
      const field =
        this.reportingMetaDataManager.metaDataByVertical[ReportVerticalType.PLATFORM]?.fields.get(
          key
        );
      if (field) {
        fields.push(field);
      }
    });

    this.reportingParamsManager.setGroups(groups);
    this.reportingParamsManager.setFields(fields);
  }

  public onLoadingReport = () => {
    this.isLoading = true;
  };

  public onReportError = (err: any) => {
    this.alertsManager.setBasicAlert(
      { content: `We couldn\'t generate the report` },
      AlertAccent.Error
    );
    this.$injex.logger.error('onReportError', err);
    this.isLoading = false;
  };

  @computed
  public get data(): any[] {
    if (!this.dynamicFormulas.length) return this.results.result;
    return this.results.result.map(row => {
      const fields = row.fields.filter(field => field.type !== 'formula');
      const values = index => Object.fromEntries(fields.map(field =>
        [field.key, Array.isArray(field.value) ? field.value[index] : field.value]));
      const original = values(0), comparison = values(1);
      const compare = fields.some(field => Array.isArray(field.value));
      const calculated = this.dynamicFormulas.map(({field, evaluate}) => {
        const current = evaluate(original), previous = evaluate(comparison);
        const value = compare ? [current, previous, current - previous, previous ? (current - previous) / previous : 0] : current;
        return {id: `${row.id}_${field.value}`, key: field.value, name: field.label,
          type: 'formula', isRequested: true, value,
          displayValue: Array.isArray(value) ? value.map(defaultFieldFormat) : defaultFieldFormat(value)};
      });
      return {...row, fields: [...fields, ...calculated],
        indexed: {...row.indexed, ...Object.fromEntries(calculated.map(field => [field.name, field]))}};
    });
  }

  public cleanResults() {
    this.clearBaselineRow();
    this.results = this.initialResults;
  }

  public generateReportLink = () => {
    // Copies the editor's current state, so a link can be shared before a report is generated.
    const urlParams = this.generateReportUrlParamsManager.generate(
      {filters: this.reportingParamsManager.preparedFilters},
      this.reportingParamsManager.params
    );
    MemoryClipboard.copy(`${window.location.origin}/reporting?${urlParams}`);
    this.alertsManager.setBasicAlert({ content: `Link copied to clipboard` }, AlertAccent.Success);
  };

  public connectHooks() {
    this.historyReportsManager.hooks.onReportReady.tap(this.onReportReady);
    this.regularReportsManager.hooks.onReportReady.tap(this.onReportReady);

    this.historyReportsManager.hooks.onReportError.tap(this.onReportError);
    this.regularReportsManager.hooks.onReportError.tap(this.onReportError);

    this.historyReportsManager.hooks.onLoadingReport.tap(this.onLoadingReport);
  }

  private setGenerateReportStatus = () => {
    let secondsLeft = 10;
    const disabledTimer = setInterval(() => {
      if (!this.isLoading || secondsLeft <= 0) {
        clearInterval(disabledTimer);
        return (this.isGenerateReportDisabled = false);
      }
      this.isGenerateReportDisabled = true;
      secondsLeft -= 1;
    }, 1000);
  };

  public downloadCSV = () => {
    this.alertsManager.setBasicAlert(
      { content: `Generating CSV, Please wait...` },
      AlertAccent.Info
    );
    this.exportToCsvManager.exportReport(
      this.data,
      this.reportingParamsManager.extractedParamsFields
    );
  };

  private get initialResults(): IReportingResults {
    return {
      result: observable([]),
      originResults: [],
      fields: [],
      groups: [],
      totalFields: {},
    };
  }

  public toggleUseHistoryReports = () => {
    if (this.useHistoryReports) {
      this.routerService.push(UrlsService.reporting());
    }

    this.useHistoryReports = !this.useHistoryReports;
  };

  public setInitialParamsFromQuery = async (query: string, id?: string) => {
    if (id) {
      this.isLoading = true;
      this.historyReportsColumnsManager.items.hooks.fetchCompleted.tap(() =>
        this.findItemAndSetSelectedReport(id)
      );
    } else {
      await this.reportingParamsManager.setInitialParams(query);
    }
  };

  public findItemAndSetSelectedReport = (id: string) => {
    const selectedItem = this.historyReportsColumnsManager.items.data.find(
      (item) => item._id === id
    );

    if (selectedItem) {
      if (selectedItem.status === ReportHistoryStatus.Finished) {
        this.historyReportsManager.getReportFromReportingHistoryUrls(
          selectedItem,
          HistoryReportGetUrlAction.Reuse
        );
      }
    }
  };

  @computed
  public get maskItemsTotal(): Record<string, string | number> {
    const totalByField = this.maskItems.reduce((acc, item) => {
      item.fields.forEach((field) => {
        if (!acc[field.key]) {
          acc[field.key] = 0;
        }
        if (field.value) {
          acc[field.key] += Array.isArray(field.value) ? field.value[0] : field.value;
        }
      });

      return acc;
    }, {});

    // Formulas must use numbers, even after earlier columns have been formatted.
    const rawTotals = {...totalByField};
    Object.keys(totalByField).forEach((key) => {
      const fieldFromContext = this.reportingMetaDataManager.getFieldByValue(
        key,
        this.reportingParamsManager.params.verticalType
      );

      if (!fieldFromContext) {
        return;
      }
      const format = fieldFromContext.format || defaultFieldFormat;
      const formula = fieldFromContext.formula;

      switch (fieldFromContext.type) {
        case ReportingFieldType.SUMABLE:
          break;
        case ReportingFieldType.AVERAGABLE:
          totalByField[key] = rawTotals[key] / this.maskItems.length;
          break;
        case ReportingFieldType.CALCULATED:
          totalByField[key] = formula ? formula(rawTotals) : rawTotals[key];
          break;
      }

      totalByField[key] = format(totalByField[key]);
    });

    this.dynamicFormulas.forEach(({field, evaluate}) => {
      totalByField[field.value] = defaultFieldFormat(evaluate(rawTotals));
    });
    return totalByField;
  }
}
